/**
 * W8-PARTNER-ONBOARDING (D446, ADR-0073) — the framework-neutral view-models behind Empresas' three onboarding panels:
 *
 *  - **Parceiros (balcão)** (P-03, P-02): what the operator may do on a partner account (the server's PLATFORM words:
 *    `partner_account:view|manage`, `partner_limit:propose|approve`), and whether it may decide a pending spend line —
 *    never its own (`SELF_APPROVAL`, four eyes is the identity check, the server re-checks it).
 *  - **Colocar loja no ar** (M-12, M-02): the ordered checklist of the existing routes a store goes live through
 *    (merchant → unit → storefront profile → opening hours → payout account → menu → pickup point → the store's
 *    responsible person → submit to review → two reviewers → activate; D849 added the hours and the responsible person), each step done or not from what the server answered, and the first step still open.
 *  - **Equipe da loja** (M-11): who may manage a store's team from the Admin (`merchant_staff:manage` on a GLOBAL grant).
 *
 * The server authorizes every call; this only decides what the console offers, and explains a refusal in words.
 */
import type { OperatorGrant } from '@vello/api-client';
import type { DomainPermission } from '@vello/rbac';
import { grantsAllow } from './console-access.js';

export interface PartnerAccountActions {
  readonly mayView: boolean;
  readonly mayManage: boolean;
  readonly mayPropose: boolean;
  readonly mayApprove: boolean;
  /** H1M (Q-131): propose / approve a fixed fare of a quick-order zone (four eyes; `partner_fare:*`). */
  readonly mayProposeFare: boolean;
  readonly mayApproveFare: boolean;
}

/** What the console offers on partner accounts. An unknown authority (`undefined`) offers everything the server decides. */
export function partnerAccountActions(grants: readonly OperatorGrant[] | undefined): PartnerAccountActions {
  if (grants === undefined) return { mayView: true, mayManage: true, mayPropose: true, mayApprove: true, mayProposeFare: true, mayApproveFare: true };
  const allow = (permission: DomainPermission): boolean => grantsAllow(grants, { permission, target: 'PLATFORM' });
  return { mayView: allow('partner_account:view'), mayManage: allow('partner_account:manage'), mayPropose: allow('partner_limit:propose'), mayApprove: allow('partner_limit:approve'), mayProposeFare: allow('partner_fare:propose'), mayApproveFare: allow('partner_fare:approve') };
}

export type LimitDecisionBlock = 'SELF_APPROVAL' | 'NO_PERMISSION' | 'NOT_PENDING';

/** Whether this operator may decide a line — and, when not, why (Approve is then hidden with the reason). */
export function limitDecision(change: { readonly status: string; readonly proposedBy: string }, meId: string | undefined, actions: PartnerAccountActions, selfApproval = false): { readonly allowed: boolean; readonly block?: LimitDecisionBlock } {
  if (change.status !== 'PENDING') return { allowed: false, block: 'NOT_PENDING' };
  if (!actions.mayApprove) return { allowed: false, block: 'NO_PERMISSION' };
  if (!selfApproval && meId !== undefined && meId === change.proposedBy) return { allowed: false, block: 'SELF_APPROVAL' };
  return { allowed: true };
}

export const STORE_LIVE_STEPS = ['merchant', 'unit', 'profile', 'hours', 'payout', 'menu', 'pickup', 'owner', 'submitted', 'reviewed', 'active'] as const;
export type StoreLiveStep = (typeof STORE_LIVE_STEPS)[number];

/** What the server answered about a store being put live (absent = not read yet / not there). */
export interface StoreLiveFacts {
  readonly merchantStatus?: 'draft' | 'in_review' | 'active' | 'suspended' | 'rejected';
  readonly payoutAccountRef?: string;
  readonly locationId?: string;
  readonly profilePublished?: boolean;
  /** D849 (P0-013): the profile holds at least one open window. A store with no hours is never "open". */
  readonly hoursSet?: boolean;
  /** D849 (P0-009): the store has an active owner or manager on its team (someone who can sign in to the Loja and run it). */
  readonly ownerSet?: boolean;
  readonly menuId?: string | null;
  readonly pickupPointSet?: boolean;
  /** The merchant's newest onboarding submission status in the Food Ops review queue. */
  readonly reviewStatus?: string;
}

export interface StoreLiveProgress {
  readonly steps: readonly { readonly step: StoreLiveStep; readonly done: boolean }[];
  /** The first step still open (undefined once the store is live). */
  readonly next?: StoreLiveStep;
  /** Every step before `submitted` is done: the submit may be offered. */
  readonly readyToSubmit: boolean;
}

/** The ordered checklist of putting a store live, from what the server answered. */
export function storeLiveProgress(f: StoreLiveFacts): StoreLiveProgress {
  const beyondDraft = f.merchantStatus === 'in_review' || f.merchantStatus === 'active' || f.merchantStatus === 'suspended';
  const done: Record<StoreLiveStep, boolean> = {
    merchant: f.merchantStatus !== undefined,
    unit: f.locationId !== undefined,
    profile: f.profilePublished === true,
    hours: f.hoursSet === true,
    payout: f.payoutAccountRef !== undefined && f.payoutAccountRef !== '',
    menu: typeof f.menuId === 'string' && f.menuId !== '',
    pickup: f.pickupPointSet === true,
    owner: f.ownerSet === true,
    submitted: beyondDraft,
    reviewed: f.reviewStatus === 'approved' || f.merchantStatus === 'active',
    active: f.merchantStatus === 'active',
  };
  const steps = STORE_LIVE_STEPS.map((step) => ({ step, done: done[step] }));
  const next = steps.find((s) => !s.done)?.step;
  const readyToSubmit = (['merchant', 'unit', 'profile', 'hours', 'payout', 'menu', 'pickup', 'owner'] as const).every((s) => done[s]) && f.merchantStatus === 'draft';
  return { steps, ...(next !== undefined ? { next } : {}), readyToSubmit };
}

/** Whether the Admin may manage a store's team (the platform's `merchant_staff:manage`, a MERCHANT route a GLOBAL grant covers). */
export function mayManageStoreTeam(grants: readonly OperatorGrant[] | undefined): boolean {
  return grants === undefined || grantsAllow(grants, { permission: 'merchant_staff:manage', target: 'PLATFORM' });
}

/** Whether the Admin may run the store-live flow (`merchant:onboard`) and put a reviewed store live (`merchant:approve`). */
export function storeOnboardingActions(grants: readonly OperatorGrant[] | undefined): { readonly mayOnboard: boolean; readonly mayActivate: boolean; readonly mayPublishMenu: boolean } {
  if (grants === undefined) return { mayOnboard: true, mayActivate: true, mayPublishMenu: true };
  const allow = (permission: DomainPermission): boolean => grantsAllow(grants, { permission, target: 'PLATFORM' });
  // D391 #6: only the merchant (or an admin holding `catalog:publish`) writes a catalog — MERCHANT_OPS reads it.
  return { mayOnboard: allow('merchant:onboard'), mayActivate: allow('merchant:approve'), mayPublishMenu: allow('catalog:publish') };
}

/** Parse a coordinate typed by an operator (a dot or a comma decimal); undefined when not a finite number in range. */
export function parseCoordinate(text: string, kind: 'latitude' | 'longitude'): number | undefined {
  const t = text.trim().replace(',', '.');
  if (!/^-?\d{1,3}(\.\d{1,8})?$/.test(t)) return undefined;
  const n = Number(t);
  const max = kind === 'latitude' ? 90 : 180;
  return Number.isFinite(n) && n >= -max && n <= max ? n : undefined;
}

/**
 * D849 (P0-009): whether the Admin may create a store's responsible person end to end - a MERCHANT sign-in (`operator:manage` at the
 * person's country) and the place on the store's team (`merchant_staff:manage`). Without both, the step shows who to ask.
 */
export function mayInviteStoreResponsible(grants: readonly OperatorGrant[] | undefined): boolean {
  if (grants === undefined) return true;
  return grantsAllow(grants, { permission: 'merchant_staff:manage', target: 'PLATFORM' }) && grantsAllow(grants, { permission: 'operator:manage', target: 'COUNTRY' });
}

/** True when a team has someone who can run the store: an active owner or manager. */
export function hasStoreResponsible(memberships: readonly { readonly role: string; readonly revokedAt?: string }[]): boolean {
  return memberships.some((m) => m.revokedAt === undefined && (m.role === 'owner' || m.role === 'manager'));
}
